// /reports wire schemas — the engine's biggest surface.
import { areaCaptureIdSchema, areaCaptureSchema, areaCaptureUpsertSchema, assetSchema, audioNoteSchema, CONFIANZA_FALTA_CODES, constanciaSchema, evidenceRequirementSchema, evidenceSchema, findingIdSchema, findingSchema, partiesSchema, reportCreateSchema, reportGeoSchema, reportIdSchema, reportSchema, reportStatusSchema, reportTokenIdSchema, reportUpdateSchema, signaturesSchema, typedNoteSchema, voiceNoteSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
import { areaConditionSnapshotEntrySchema } from "./area-condition.js";
import { brandSummarySchema } from "./brands.js";
import { paginated } from "./common.js";
export const reportParams = z.object({ id: reportIdSchema });
export const reportListQuery = z.object({
    // The full lifecycle except `discarded`, derived from the engine's
    // reportStatusSchema rather than hand-copied — a hand-rolled copy of
    // this exact enum previously lagged behind the engine's (#0006), which
    // made reports stuck in any analysis state vanish from /cartera's
    // resume card. Deriving means a new engine status (e.g. ADR-0098's
    // "closed") is filterable here automatically, with no drift possible.
    // `discarded` stays excluded deliberately — it was never a valid
    // filter value on this endpoint and re-adding it is a separate,
    // pre-existing gap unrelated to ADR-0098.
    status: reportStatusSchema.exclude(["discarded"]).optional(),
    assetId: z.string().uuid().optional(),
    // Filter by the asset's group — useful for the b2b vertical where
    // "asset group" maps to "project" (e.g. all reports for a building).
    assetGroupId: z.string().uuid().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().optional(),
});
// Author is the authenticated user; org is the user's org. Both are stripped
// from the body to prevent spoofing.
export const reportCreateBody = reportCreateSchema.omit({
    organizationId: true,
    authorUserId: true,
    // address is also denormalized server-side from the asset; clients pass
    // assetId only.
    address: true,
    unit: true,
});
export const reportUpdateBody = reportUpdateSchema;
// ── Read-side wire shapes ──────────────────────────────────────────────
//
// The engine entity schemas double as write-input schemas, so a column
// that is simply absent on input is declared `.optional()` there. On the
// READ side core-api serialises Drizzle rows as-is, and a nullable column
// comes back as `null`, not as a missing key — `.optional()` alone rejects
// it. No client parsed these responses until the dashboard's /reportes/:id
// did (ADR-0021 D6), which is how the drift stayed invisible: two real
// closed actas on dev failed ingress on `summary: null`, `signedAt: null`,
// `humanDescription: null`, … (2026-09-25). Each `*Wire` schema below is
// the entity with its nullable-column fields widened to `.nullable()` —
// derived, never hand-copied, so a field added to the engine still flows
// through. Keep the key lists aligned with the nullable columns in
// apps/core-api/src/db/schema.ts.
function widenNullable(schema, keys) {
    const shape = schema.shape;
    const patch = Object.fromEntries(keys.map((k) => {
        const field = shape[k];
        if (!field)
            throw new Error(`widenNullable: "${k}" is not a field of the schema`);
        return [k, field.nullable()];
    }));
    return schema.extend(patch);
}
export const reportWireSchema = widenNullable(reportSchema, [
    "unit",
    "relatedReportId",
    "summary",
    "pdfUrl",
    "signedAt",
    "closedAt",
    "signedGeo",
    "constanciaNoFirmaMotivo",
    "constanciaNoFirmaRole",
    "constanciaNoFirmaAt",
    "folio",
]).extend({
    // ADR-0138 — per-area condition frozen at sign/close for report types
    // whose recipe declares stateSource "area_condition". NULL until
    // terminal, and on every other report (and on actas closed before the
    // column existed — those read their condition live / from legacy
    // findings via core-engine's resolveAreaConditions).
    areaConditionSnapshot: z.array(areaConditionSnapshotEntrySchema).nullable().optional(),
});
export const findingWireSchema = widenNullable(findingSchema, [
    "reportId",
    "element",
    "aiDescription",
    "aiTitle",
    "humanDescription",
    "audioTranscript",
    "observedAt",
]);
export const evidenceWireSchema = widenNullable(evidenceSchema, [
    "objectKey",
    "body",
    "authorType",
]);
export const areaCaptureWireSchema = widenNullable(areaCaptureSchema, ["errorMessage"]);
export const reportResponse = reportWireSchema;
export const reportListResponse = paginated(reportWireSchema);
// Detail bundles findings, evidence, and the capture buffer so the
// review/sign UI is a single round-trip.
// `brand` carries the asset's effective brand (ADR-0033 §C1) so the
// Acta firmada hero renders the brand logo without a second round-trip.
// Null for verticals without brands (arriendos, postventa) or when the
// asset has no brand association.
export const reportDetailResponse = z.object({
    report: reportWireSchema,
    findings: z.array(findingWireSchema),
    evidence: z.array(evidenceWireSchema),
    areaCaptures: z.array(areaCaptureWireSchema),
    brand: brandSummarySchema.nullable(),
});
// POST /reports/:id/area-captures — upsert a capture for one area.
export const areaCaptureUpsertBody = areaCaptureUpsertSchema.omit({ reportId: true });
export const areaCaptureResponse = areaCaptureSchema;
// POST /reports/:id/area-captures/notes — append ONE note to a section's
// audio_notes, idempotent on note.id (ADR-0108). Derived per-arm and
// re-unioned rather than derived from audioNoteSchema directly —
// ZodDiscriminatedUnion supports neither .omit() nor .extend() nor
// .pick() (ADR-0096 §Decision 2, which made both arms public exports for
// exactly this). checklistItemId stays INSIDE the note on both arms
// (ADR-0096 §Decision 1) — deliberately unlike areaCaptureUpsertSchema's
// per-note shape, since this route's path carries no :itemId to source
// item identity from instead.
export const areaCaptureNoteAppendBody = z
    .object({
    areaName: areaCaptureUpsertSchema.shape.areaName,
    areaOrder: areaCaptureUpsertSchema.shape.areaOrder,
    note: z.discriminatedUnion("kind", [voiceNoteSchema, typedNoteSchema]),
})
    .strict();
export const areaCaptureNoteAppendResponse = z.object({
    areaCaptureId: areaCaptureIdSchema,
    noteId: z.string().min(1).max(64),
    created: z.boolean(),
    audioNotes: z.array(audioNoteSchema),
    capturedAudioNoteIds: z.array(z.string()).nullable(),
});
// DELETE /reports/:id/area-captures/notes/:noteId — retraction (ADR-0108
// §Decision 6). Always 200: `deleted` is the diagnostic discriminator,
// mirroring `created` above — the PWA drain loop treats any 2xx as
// terminal and never branches on status code (§Decision 3), so a 404 on
// a replayed delete would strand the queue row in `failed` forever.
export const areaCaptureNoteDeleteParams = reportParams.extend({
    noteId: z.string().min(1).max(64),
});
export const areaCaptureNoteDeleteResponse = z.object({
    areaCaptureId: areaCaptureIdSchema.nullable(), // null on the no-op (note-already-absent) path
    noteId: z.string().min(1).max(64),
    deleted: z.boolean(),
    audioNotes: z.array(audioNoteSchema),
    capturedAudioNoteIds: z.array(z.string()).nullable(),
});
// POST /reports/:id/upload-photo — append one photo URL to the area capture.
// Actual upload happens in media-svc; this endpoint records the result.
export const uploadPhotoBody = z.object({
    // #0578 — derived from the engine schema (invariant: wire schemas
    // derive from engine schemas via omit/extend/pick/merge, never a
    // hand-rolled mirror), matching the sibling areaCaptureNoteAppendBody
    // above. Previously hand-rolled here, independently of and identical
    // to areaCaptureUpsertSchema.shape.areaName/.areaOrder — a drift
    // waiting to happen, not yet the cause of a bug.
    areaName: areaCaptureUpsertSchema.shape.areaName,
    areaOrder: areaCaptureUpsertSchema.shape.areaOrder,
    photoUrl: z.string().url(),
    /** ADR-0088 — which checklist item this photo is evidence for.
     *  Optional: omitted means section-level evidence (today's behavior,
     *  unchanged for residential_rental / broker_postventa, which have no
     *  checklist items at all). Must ride in the SAME request that commits
     *  the photo, not a follow-up call — the offline-queue pre-flush path
     *  interleaves requests, and a two-call attribution scheme would let a
     *  partial flush produce an un-attributable photo. */
    checklistItemId: z.string().uuid().optional(),
});
// POST /reports/:id/complete — finalize capture and (eventually) trigger
// AI processing. Body intentionally empty for now.
export const reportCompleteBody = z.object({}).strict();
// POST /reports/:id/sign — write parties + signatures and transition to
// `signed`. parties may already have been saved via PUT /reports/:id; if
// passed here they overwrite. signatures are required.
//
// constancia (ADR-0028, H-03) — optional. Required when any signature
// is declined AND the recipe's signatureRole has declineReasonCapture.
// Engine's validateConstancia() enforces the relationship. When present
// the route writes the three reports.constancia_no_firma_* columns +
// appends an audit_events row in the same transaction.
export const reportSignBody = z.object({
    parties: partiesSchema.optional(),
    signatures: signaturesSchema,
    constancia: constanciaSchema.optional(),
    // Sign-time geolocation (#38). Derived from the engine geo schema so
    // the wire shape can't drift from contextMetadata.geo. Optional —
    // capture-with-flag; the report still signs without it. Persisted into
    // reports.signed_geo co-transactionally with signedAt.
    signedGeo: reportGeoSchema.optional(),
});
// POST /reports/:id/close (ADR-0098 Decision 2, #0509) — attestation-based
// closure. Accepts parties only; never signatures, constancia or
// signedGeo (report-level geo is superseded by the per-section verdict at
// #0511). Built directly from partiesSchema — the same engine schema
// reportSignBody's `parties` field derives from — rather than as
// `reportSignBody.omit({signatures, constancia, signedGeo})`: an
// omit-chain couples /close's contract to /sign's, so a field added to
// /sign later would silently reach /close too unless someone remembers to
// extend the deny-list.
// .strict() so a client key typo (`partys`) fails loudly instead of being
// silently stripped into an empty body on a terminal write — matches
// hallazgoCreateBody's posture below.
export const reportCloseBody = z
    .object({
    parties: partiesSchema.optional(),
})
    .strict();
// POST /reports/:id/hallazgos (ADR-0058 v2, postventa acta_entrega) —
// an ad-hoc AI-classified finding, matching the reference app's
// freestanding "+ Registrar hallazgo" flow. Photos are already
// uploaded to R2 via POST /media/presign-upload; this endpoint takes
// the resulting public URLs plus an optional already-transcribed note
// (Web Speech API, client-side) and returns the finding(s) Claude
// classified from them.
export const hallazgoCreateBody = z
    .object({
    note: z.string().trim().max(4000).optional(),
    photoUrls: z.array(z.string().url()).min(1).max(10),
})
    .strict();
export const hallazgoCreateResponse = z.object({
    findings: z.array(findingSchema),
});
// ─── Per-block routes (ADR-0025 / facility A→B-per-bloque) ────────────
export const reportBlockParams = z.object({
    id: reportIdSchema,
    areaId: z.string().uuid(),
});
// POST /reports/:id/blocks/:areaId/complete — supervisor closes
// captura A on a bloque. Body intentionally empty: the area_capture
// row already carries photos + audio (uploaded via /upload-photo +
// /area-captures). This route flips capture status to "analyzing"
// and enqueues JOB_ANALYZE_BLOCK.
export const blockCompleteBody = z.object({}).strict();
// 409 error shape from POST /reports/:id/blocks/:areaId/complete when
// deriveSectionClosable() (core-engine/src/blocks.ts) is false — ADR-0088.
// The error is enumerated (missingItems), not just a count: the PWA is
// offline-first, so its own client-side "cerrable" read can legitimately
// disagree with the server (an evidence write queued locally never
// replayed) — without knowing WHICH items, the client can only show
// "faltan ítems" with no way to reconcile. missingItemIds is kept one
// release for PWA tabs on the pre-ADR-0088 build; missingItems is the
// real payload. No user-facing Spanish here — es-CL copy is
// apps/web-svelte's source of truth (CLAUDE.md §5); the FE composes the
// sentence from required/hasPhoto/hasAudio.
export const sectionEvidenceIncompleteError = z.object({
    code: z.literal("SECTION_EVIDENCE_INCOMPLETE"),
    message: z.string(),
    /** @deprecated derived from missingItems[].itemId — kept one release. */
    missingItemIds: z.array(z.string().uuid()),
    missingItems: z.array(z.object({
        itemId: z.string().uuid(),
        itemKey: z.string().min(1),
        label: z.string().min(1),
        required: evidenceRequirementSchema,
        hasPhoto: z.boolean(),
        hasAudio: z.boolean(),
    })),
});
// POST /reports/:id/blocks/:areaId/reprocess — supervisor re-runs AI
// on a bloque after capturing more evidence in B. Same enqueue, same
// singleton key — pg-boss serializes if the previous job is still
// active.
export const blockReprocessBody = z.object({}).strict();
// PATCH /reports/:id/blocks/:areaId/items/:itemId — supervisor adjusts
// a per-item verdict in revisión B. Only mutable fields are listed;
// the AI-proposed fields (ai_proposed_verdict, ai_description) are
// preserved as the audit trail of what the model said.
export const blockItemParams = reportBlockParams.extend({
    itemId: z.string().uuid(),
});
export const blockItemUpdateBody = z
    .object({
    supervisorVerdict: z.enum(["cumple", "con_obs", "no_cumple", "n_a"]),
    severity: z.enum(["leve", "moderada", "critica"]).optional(),
    humanDescription: z.string().max(2000).optional(),
})
    .strict();
// DELETE /reports/:id/blocks/:areaId/items/:itemId/evidence — ADR-0088
// follow-up. Retracts one piece of per-item evidence (a photo or a voice
// note) from both the item attribution and the section-scoped buffer.
// `value` is the photoUrl for kind:"photo" or the audio note id for
// kind:"audio" — matches area_captures.photoUrls[] / audioNotes[].id.
// Unchanged by ADR-0096 (nota escrita): this route matches by note id
// alone and never inspects `kind`, so it already deletes a typed note
// correctly with zero code change.
export const blockItemEvidenceDeleteBody = z.object({
    kind: z.enum(["photo", "audio"]),
    value: z.string().min(1),
});
// PATCH /reports/:id/blocks/:areaId/items/:itemId/notes/:noteId — ADR-0096
// (nota escrita). Overwrites a TYPED note's text in place. Voice notes are
// not editable server-side (their existing "Editar" affordance stays the
// pre-existing local-only transcript correction — see the FE for that).
// Deliberately narrow: this is not a general note-PATCH, it exists to
// give a typed note the same in-place-correction affordance the product
// requires, with a guard chain a plain reuse of the DELETE-evidence
// route's shape would not have (that route omits ensureAuthor and
// rejectIfSectionClosed — copying it for edit would let a non-author
// rewrite another inspector's note, or edit text an AI verdict already
// cited). The route also rejects once the bloque has moved past
// "capturando" (rejectIfSectionClosed) specifically so a persisted AI
// verdict can never cite text that no longer exists — no separate
// verdict-invalidation logic is needed because the two states are made
// mutually exclusive by construction.
export const blockItemNoteUpdateBody = z
    .object({
    text: z.string().trim().min(1).max(2_000),
})
    .strict();
export const blockItemNoteParams = blockItemParams.extend({
    noteId: z.string().min(1).max(64),
});
// GET /reports/:id/blocks — returns derived per-bloque state for the
// procesando / revisión screens. Engine's deriveBlockLifecycle()
// computes the 5-state enum from the raw rows server-side.
export const blockStateSchema = z.object({
    inspectionAreaId: z.string().uuid(),
    /** inspection_areas.key — stable slug for FE branching. */
    blockKey: z.string().min(1).max(64),
    /** inspection_areas.label — display string. */
    label: z.string().min(1),
    displayOrder: z.number().int().nonnegative(),
    totalItems: z.number().int().nonnegative(),
    reviewedItems: z.number().int().nonnegative(),
    lifecycle: z.enum(["pendiente", "capturando", "procesando_ia", "revisando", "cerrado"]),
    /** Raw area_captures.status for this bloque. NULL when no capture
     *  row exists yet (lifecycle = 'pendiente'). Exposed so the FE can
     *  surface 'analysis_failed' as a retry affordance — deriveBlockLifecycle
     *  masks the failed state as 'capturando' (the supervisor's "next action"
     *  is the same), but on the /procesando screen we need the granular
     *  signal so the bar doesn't sit at 92% for 5 min waiting for a job
     *  that's already dead. */
    captureStatus: z.enum(["pending", "analyzing", "analyzed", "analysis_failed"]).nullable(),
    /** area_captures.error_message when status='analysis_failed', else
     *  null. Shown in the FE retry banner. */
    captureErrorMessage: z.string().nullable(),
    /** ADR-0088 — server-derived from core-engine's deriveSectionClosable().
     *  True when every required item in this bloque satisfies its own
     *  evidenceRequirement. The FE gates its "Cerrar sección" CTA on this,
     *  never by re-deriving the predicate client-side — see
     *  evidenceSatisfied on blockItemStateSchema for the same rule at the
     *  item level. */
    cerrable: z.boolean(),
    /** Count of required items whose evidenceRequirement is satisfied.
     *  Feeds the "2/6" progress counter with no extra round trip. */
    evidenceSatisfiedItems: z.number().int().nonnegative(),
});
export const blocksListResponse = z.object({
    blocks: z.array(blockStateSchema),
    /** True when every bloque the recipe requires is `cerrado`. The FE
     *  gates "Cerrar y firmar" on this. */
    readyToSign: z.boolean(),
});
// GET /reports/:id/area-templates — the closed, per-unit recinto catalog
// for a reportType with areaCatalogMode "predefined_per_asset" (ADR-0056).
// Unlike /blocks (org-scoped, facility-only), this is asset+reportType
// scoped — every row is implicitly required, ordered for full traversal.
export const areaTemplateStateSchema = z.object({
    name: z.string().min(1),
    displayOrder: z.number().int().nonnegative(),
});
export const areaTemplatesListResponse = z.object({
    templates: z.array(areaTemplateStateSchema),
});
// GET /reports/:id/blocks/:areaId/items — items + per-item state for
// the revisión B screen. Server joins inspection_checklist_items
// with report_checklist_results so the FE renders one row per item
// with AI proposed verdict, supervisor verdict, severity and the
// description pair.
export const blockItemStateSchema = z.object({
    /** PK of inspection_checklist_items. */
    itemId: z.string().uuid(),
    /** Stable slug. */
    itemKey: z.string().min(1),
    label: z.string().min(1),
    displayOrder: z.number().int().nonnegative(),
    /** AI-proposed verdict (cumple | con_obs | no_cumple). NULL when
     *  analyze-block hasn't yet written a row or wrote one without a
     *  verdict — should be rare in practice. */
    aiProposedVerdict: z.enum(["cumple", "con_obs", "no_cumple"]).nullable(),
    /** Supervisor's confirmed/adjusted verdict. NULL = not yet reviewed. */
    supervisorVerdict: z.enum(["cumple", "con_obs", "no_cumple", "n_a"]).nullable(),
    /** Severity when verdict ≠ cumple. */
    severity: z.enum(["leve", "moderada", "critica"]).nullable(),
    aiDescription: z.string().nullable(),
    humanDescription: z.string().nullable(),
    /** Evidence-attribution arrays from analyze-block (ADR-0025). The
     *  AI cites which photos and audio notes drove its verdict; the FE
     *  joins these against blockItemsResponse.photoUrls / .audioNotes
     *  to render per-item thumbnails + transcripts on revisión B.
     *  NULL when the model didn't cite any evidence. */
    aiPhotoIndices: z.array(z.number().int().nonnegative()).nullable(),
    aiAudioNoteIds: z.array(z.string().min(1)).nullable(),
    /** ADR-0088 — what evidence this item demands before its bloque can
     *  close. 'none' on recipes/items that don't use the feature. */
    evidenceRequirement: evidenceRequirementSchema,
    /** ADR-0088 — evidence the SUPERVISOR attached to this item at capture
     *  time (captura A), by value (URL / note id), never by index — see
     *  the schema comment on report_checklist_results.captured_photo_urls
     *  for why. Distinct from aiPhotoIndices/aiAudioNoteIds above, which
     *  are the model's post-hoc citations computed later by analyze-block. */
    capturedPhotoUrls: z.array(z.string()).default([]),
    capturedAudioNoteIds: z.array(z.string().min(1)).default([]),
    /** Server-derived from core-engine's itemEvidenceSatisfied(). The FE
     *  renders the per-item done state off this boolean, never by
     *  re-implementing the predicate against evidenceRequirement +
     *  capturedPhotoUrls.length/capturedAudioNoteIds.length itself —
     *  scripts/fitness/evidence-predicate-single-implementation.mjs
     *  enforces that. */
    evidenceSatisfied: z.boolean(),
});
// Audio-note wire shape on the items response. Derived from core-engine's
// audioNoteSchema (ADR-0088) rather than hand-rolled, so a field added
// there (checklistItemId) can't silently drift out of sync with what the
// FE actually receives — this file was previously a manual mirror and
// had already drifted once (no checklistItemId) before this fix.
export const blockAudioNoteSchema = audioNoteSchema;
export const blockItemsResponse = z.object({
    inspectionAreaId: z.string().uuid(),
    blockKey: z.string().min(1),
    label: z.string().min(1),
    items: z.array(blockItemStateSchema),
    /** Photo URLs from the matching area_capture, ordered. The
     *  aiPhotoIndices on each item are 0-based offsets into this array
     *  so the FE can render per-item thumbnails without a second
     *  round-trip. Empty when no photos were captured. */
    photoUrls: z.array(z.string()).default([]),
    /** Audio notes from the matching area_capture. aiAudioNoteIds on
     *  each item reference the id field; the FE filters this list per
     *  item. Empty when no notes were captured. */
    audioNotes: z.array(blockAudioNoteSchema).default([]),
});
// GET /reports/:id/pdf-data — everything the client renderer needs.
// Includes the asset row so the renderer has access to address-level
// metadata (commune, owner_*, property_type stored in attributes JSONB)
// without a second round-trip.
export const reportPdfDataResponse = reportDetailResponse.extend({
    asset: assetSchema,
    // Attestor identity for the acta face (founder ruling 2026-08-28):
    // a closed acta renders the closer's display name + real member-role
    // key (label resolved client-side from recipe.memberRoles), never a
    // RUT. Facility parties carry no identity (ADR-0040); attribution
    // lives in reports.closed_by_user_id and surfaces here read-time.
    // Null pre-close, on the signature-closure verticals, or when the
    // tolerant cross-service auth lookup fails. Authenticated-path only —
    // deliberately NOT on the public /r/<token> projection.
    closedBy: z
        .object({ name: z.string().nullable(), role: z.string().nullable() })
        .nullable()
        .default(null),
});
// POST /reports/:id/share — generate (or reuse) a public token + URL.
export const reportShareBody = z
    .object({
    sendEmail: z.boolean().default(false),
})
    .strict();
export const reportShareResponse = z.object({
    tokenId: reportTokenIdSchema,
    token: z.string().min(32).max(128),
    url: z.string().url(),
});
// GET /reports/:id/compare — pair this report with its check-in (if this
// is a check-out) or check-out (if this is a check-in) on the same asset
// and return a per-recinto split for the comparator screen.
//
// Pair resolution: prefer `report.relatedReportId` if set; otherwise auto-
// resolve to the most recent SIGNED report of the opposite type for the
// same asset. The endpoint 404s if no pair exists or the recipe doesn't
// declare check_in / check_out (arriendos-only feature). `diff` per
// recinto is derived deterministically from severity rank; the summary
// bullets are an honest stub until ai-svc grows a compare-narrative job.
export const compareSlotSchema = z.object({
    category: z.string(),
    severity: z.string(),
    description: z.string(),
    element: z.string().optional(),
    photoUrl: z.string().url().optional(),
});
export const compareRecintoSchema = z.object({
    areaName: z.string(),
    areaOrder: z.number().int().nonnegative(),
    // "same" — both sides bueno / no change; "low" — out side is desgaste;
    // "mid" — out side is dano; "high" — out side is faltante. Matches the
    // `.compare-diff--{same,low,mid,high}` modifiers from S6.
    diff: z.enum(["same", "low", "mid", "high"]),
    inSlot: compareSlotSchema.nullable(),
    outSlot: compareSlotSchema.nullable(),
    // ADR-0138 — each side's area condition (a recipe severity key: the
    // frozen snapshot, or legacy «Buen estado» findings on older actas).
    // Null when that side recorded none; absent from older servers.
    inCondition: z.string().nullable().optional(),
    outCondition: z.string().nullable().optional(),
});
export const compareSummaryItemSchema = z.object({
    // "diff" — severity worsened (dano); "miss" — faltante introduced;
    // "ok" — attributable to normal wear. Glyph + tinta per S6.
    kind: z.enum(["diff", "miss", "ok"]),
    text: z.string(),
});
export const compareSummarySchema = z.object({
    title: z.string(),
    meta: z.string(),
    bullets: z.array(compareSummaryItemSchema),
});
export const compareReportPartySchema = z.object({
    id: reportIdSchema,
    reportType: z.string(),
    signedAt: z.string().optional(),
});
export const reportCompareResponse = z.object({
    in: compareReportPartySchema,
    out: compareReportPartySchema,
    address: z.string(),
    unit: z.string().optional(),
    recintos: z.array(compareRecintoSchema),
    summary: compareSummarySchema,
});
// ── Confianza gate (ADR-0119, PR-2) ─────────────────────────────────
// One payload shape: GET /reports/:id/confianza-pendientes and (PR-3)
// the `falta` field of GET /findings/:id. Since 2026-10-02 POST
// /reports/:id/sign no longer returns the 409 CONFIANZA_REVIEW_REQUIRED
// (founder: the acta always signs; observations are independent of it)
// — `confianzaGateDetailsSchema` below stays only so a client facing a
// pre-change server during deploy skew can still parse that 409. The
// code enum is DERIVED from the engine (invariant 3: never re-declared
// by hand); the label is rendered server-side in core-api so every
// client shows the same string.
/** El vocabulario a secas, sin etiqueta. Extraído del par de abajo
 *  (#0646) porque hay un consumidor que sólo necesita el código: el
 *  `gate.falta` de POST /findings/:id/reclassify, que va en las tres
 *  ramas y cuyo cliente ya trae el copy del `falta` de
 *  GET /findings/:id. Un `z.string()` allí —que es lo que el read model
 *  de findings.ts tenía— aplana el enum justo en el campo con el que el
 *  cliente decide qué pantalla de remedio abrir. */
export const confianzaFaltaCodeSchema = z.enum(CONFIANZA_FALTA_CODES);
export const confianzaFaltaSchema = z.object({
    code: confianzaFaltaCodeSchema,
    label: z.string().min(1),
});
export const confianzaPendienteSchema = z.object({
    findingId: findingIdSchema,
    folio: z.string().nullable(),
    falta: confianzaFaltaSchema,
});
/** GET /reports/:id/confianza-pendientes — informational, never blocks
 *  signing. `firmable` is kept on the wire for compatibility and now
 *  means "no classification reviews pending", not "signing allowed".
 *  `{ firmable: true, count: 0, pendientes: [] }` for recipes without
 *  observationTaxonomy, so the sign screen of every vertical can call it
 *  unbranched. */
export const confianzaPendientesResponse = z.object({
    firmable: z.boolean(),
    count: z.number().int().nonnegative(),
    pendientes: z.array(confianzaPendienteSchema),
});
/** `details` of the 409 CONFIANZA_REVIEW_REQUIRED. The `observations`
 *  key is stable (pre-ADR-0119 clients read `{ id, folio }` off it);
 *  each entry is widened, not renamed — `id` and `findingId` carry the
 *  same value. */
export const confianzaGateDetailsSchema = z.object({
    count: z.number().int().positive(),
    observations: z.array(confianzaPendienteSchema.extend({ id: findingIdSchema })),
});
//# sourceMappingURL=reports.js.map